import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Device } from '@twilio/voice-sdk';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Phone, PhoneOff, PhoneCall, Mic, MicOff, X, PhoneIncoming, FileText, ExternalLink, Delete, ArrowRightLeft, ClipboardCheck, CalendarClock, MessageSquare, Pause, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/AuthContext';
import { voiceAPI, leadsAPI, clientsAPI, smsMessagingAPI, prospectAPI } from '@/lib/api';

const LEAD_STATUS_OPTIONS = [
  { value: 'contacted', label: 'Contacted' },
  { value: 'attempting_contact', label: 'Attempting contact' },
  { value: 'quoted', label: 'Quoted' },
  { value: 'appointment', label: 'Appointment set' },
  { value: 'closed_won', label: 'Closed — Won' },
  { value: 'not_interested', label: 'Not interested' },
  { value: 'new', label: 'New' },
];

const NOT_INTERESTED_REASON_OPTIONS = [
  { value: 'price', label: 'Price' },
  { value: 'timing', label: 'Timing' },
  { value: 'competition', label: 'Competition' },
  { value: 'not_qualified', label: 'Not qualified' },
  { value: 'no_response', label: 'No response' },
  { value: 'other', label: 'Other' },
];

// Recruiting call wrap-up for prospects (how the call went).
const PROSPECT_OUTCOME_OPTIONS = [
  { value: 'connected', label: 'Connected' },
  { value: 'no_answer', label: 'No answer' },
  { value: 'voicemail', label: 'Left voicemail' },
  { value: 'busy', label: 'Busy' },
  { value: 'callback', label: 'Call back requested' },
  { value: 'wrong_number', label: 'Wrong number' },
];

// Recruiting pipeline stages a prospect can be advanced to after a call.
const PROSPECT_STAGE_OPTIONS = [
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'interview', label: 'Interview' },
  { value: 'application', label: 'Application' },
  { value: 'not_a_fit', label: 'Not a fit' },
];

// Quick call-back presets → ISO datetime (local) at 9:00 AM.
const callbackPreset = (kind) => {
  const d = new Date();
  if (kind === 'tomorrow') d.setDate(d.getDate() + 1);
  else if (kind === '3days') d.setDate(d.getDate() + 3);
  else if (kind === 'week') d.setDate(d.getDate() + 7);
  d.setHours(9, 0, 0, 0);
  return d;
};

// <input type="datetime-local"> wants "YYYY-MM-DDTHH:mm" in local time.
const toLocalInput = (d) => {
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Resolve the lead's language: explicit preference → IVR-detected → English.
const resolveLang = (preferred, ivrLang) => {
  const p = (preferred || '').toLowerCase();
  if (p.startsWith('es') || p === 'spanish' || p === 'español') return 'es';
  if (p.startsWith('en') || p === 'english') return 'en';
  const i = (ivrLang || '').toLowerCase();
  if (i.startsWith('es') || i === 'spanish' || i === 'español') return 'es';
  return 'en';
};

const formatCallbackDate = (iso, lang) => {
  try {
    return new Date(iso).toLocaleString(lang === 'es' ? 'es-ES' : 'en-US', {
      weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch (_) { return iso; }
};

// Pre-filled follow-up SMS the agent can edit before sending.
const buildFollowUpSms = (lang, firstName, callbackIso) => {
  const name = (firstName || '').trim();
  if (lang === 'es') {
    const hola = name ? `Hola ${name}, ` : 'Hola, ';
    const cb = callbackIso ? `Te llamaré de vuelta el ${formatCallbackDate(callbackIso, 'es')}. ` : '';
    return `${hola}gracias por hablar conmigo hoy. ${cb}Quedo a tus órdenes si tienes preguntas. — KR Agency`;
  }
  const hi = name ? `Hi ${name}, ` : 'Hi, ';
  const cb = callbackIso ? `I'll call you back on ${formatCallbackDate(callbackIso, 'en')}. ` : '';
  return `${hi}thanks for speaking with me today. ${cb}I'm here if you have any questions. — KR Agency`;
};

const profilePath = (type, id) => {
  if (!type || !id) return null;
  if (type === 'lead') return `/leads/${id}`;
  if (type === 'client') return `/clients/${id}`;
  if (type === 'agent') return `/agents/${id}`;
  if (type === 'prospect') return '/prospects';
  return null;
};

// Normalize free-typed input to E.164. US/PR numbers belong to the NANP
// (+1), so a bare 10-digit number or an 11-digit number starting with 1
// is auto-prefixed. A leading "+" is treated as an explicit international
// number and only lightly validated. Returns null when it can't be made
// into a valid E.164 string.
const normalizeToE164 = (raw) => {
  const s = (raw || '').trim();
  if (!s) return null;
  if (s.startsWith('+')) {
    const digits = s.slice(1).replace(/\D/g, '');
    return /^[1-9]\d{7,14}$/.test(digits) ? `+${digits}` : null;
  }
  const digits = s.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return null;
};

// Pretty US/PR display: (863) 555-1212. Falls back to raw for others.
const formatUsDisplay = (e164) => {
  if (!e164) return '';
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : e164;
};

/**
 * Phase 27.58 — Global browser softphone for KR admins.
 * Floating widget: place outbound calls, receive inbound calls that ring
 * every online admin, mute + hang up. Never exposes a personal number.
 * Other parts of the app can trigger a call via window.krSoftphoneDial(e164).
 */
export default function Softphone() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const deviceRef = useRef(null);
  const callRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [eligible, setEligible] = useState(false);
  const [phoneConfigured, setPhoneConfigured] = useState(true);
  const [callerNumbers, setCallerNumbers] = useState([]);
  const [numberNicknames, setNumberNicknames] = useState({});
  const [fromNumber, setFromNumber] = useState('');
  const fromStorageKey = user?.user_id ? `kr.softphone.from.${user.user_id}` : 'kr.softphone.from';
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState('');
  const [status, setStatus] = useState('offline');
  const [muted, setMuted] = useState(false);
  const [canPauseRec, setCanPauseRec] = useState(false);
  const [recordingEnabled, setRecordingEnabled] = useState(false);
  const [recordingPaused, setRecordingPaused] = useState(false);
  const [recPending, setRecPending] = useState(false);
  const [incoming, setIncoming] = useState(null);
  const [incomingName, setIncomingName] = useState('');
  const [callContext, setCallContext] = useState(null);
  const [match, setMatch] = useState(null);
  const [callTarget, setCallTarget] = useState(null);
  const [xfer, setXfer] = useState(null);
  const [xferAgents, setXferAgents] = useState([]);
  const [xferNumber, setXferNumber] = useState('');
  const ctxRef = useRef(null);
  const [noteFor, setNoteFor] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  // Post-call disposition (shown after a call to a CRM lead ends).
  const [dispositionRequired, setDispositionRequired] = useState(false);
  const [dispoStatus, setDispoStatus] = useState('');
  const [dispoReason, setDispoReason] = useState('');
  const [dispoOutcome, setDispoOutcome] = useState('');
  const [callbackAt, setCallbackAt] = useState('');
  const [savingDispo, setSavingDispo] = useState(false);
  // Follow-up SMS (manual toggle inside the disposition panel).
  const [smsMeta, setSmsMeta] = useState(null); // {canSms, phone, consent, lang, firstName} | null while loading
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [smsBody, setSmsBody] = useState('');
  const smsTouchedRef = useRef(false);
  const callSidRef = useRef(null);
  // Call-center availability. Persisted so a reload keeps the agent's
  // chosen state. When inactive, presence is sent as unavailable so
  // inbound calls never ring this softphone.
  const [available, setAvailable] = useState(() => {
    try { return localStorage.getItem('kr.softphone.available') !== '0'; } catch (_) { return true; }
  });
  const availableRef = useRef(available);
  useEffect(() => { availableRef.current = available; }, [available]);
  // Clear the paused-recording indicator whenever we leave an active call.
  useEffect(() => {
    if (status === 'ready' || status === 'offline') setRecordingPaused(false);
  }, [status]);
  // Report on-call state to the supervision board whenever it changes.
  useEffect(() => {
    if (!eligible || !phoneConfigured) return;
    const onCall = status === 'in-call' || status === 'calling';
    voiceAPI.presence(availableRef.current, onCall).catch(() => {});
  }, [status, eligible, phoneConfigured]);

  const getToken = useCallback(async () => {
    const r = await voiceAPI.token();
    return r.data.token;
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    let disposed = false;
    let device = null;
    let presenceTimer = null;

    (async () => {
      try {
        const s = await voiceAPI.settings();
        if (!s.data?.can_use_softphone) return; // role not allowed
        if (disposed) return;
        setEligible(true);
        setDispositionRequired(!!s.data?.disposition_required);
        setCanPauseRec(!!s.data?.can_pause_recording);
        setRecordingEnabled(!!s.data?.recording_enabled);
        setCallerNumbers(s.data?.caller_numbers || []);
        setNumberNicknames(s.data?.number_nicknames || {});
        setFromNumber((prev) => {
          if (prev) return prev;
          const nums = s.data?.caller_numbers || [];
          let saved = '';
          try { saved = localStorage.getItem(fromStorageKey) || ''; } catch (_) { /* noop */ }
          if (saved && nums.includes(saved)) return saved;
          return s.data?.default_caller_number || nums[0] || '';
        });
        if (!s.data?.configured) { setPhoneConfigured(false); return; } // allowed but not set up yet
        setPhoneConfigured(true);
        const token = await getToken();
        if (disposed) return;
        device = new Device(token, { logLevel: 'error', codecPreferences: ['opus', 'pcmu'] });
        deviceRef.current = device;
        device.on('registered', () => { setReady(true); setStatus('ready'); });
        device.on('unregistered', () => setStatus('offline'));
        device.on('error', (e) => { console.warn('Twilio Device error', e?.message); });
        device.on('tokenWillExpire', async () => {
          try { device.updateToken(await getToken()); } catch (_) { /* ignore */ }
        });
        device.on('incoming', (call) => {
          setIncoming(call);
          setOpen(true);
          const from = call.parameters?.From || '';
          // IVR context passed from the backend (language, reason chosen).
          const cp = call.customParameters;
          const ctxLang = cp?.get ? cp.get('lang') : (cp?.lang || '');
          const ctxReason = cp?.get ? cp.get('reason') : (cp?.reason || '');
          setCallContext(ctxLang || ctxReason ? { lang: ctxLang, reason: ctxReason } : null);
          ctxRef.current = { number: from, ivr_lang: ctxLang, ivr_reason: ctxReason };
          setIncomingName('');
          setMatch(null);
          callSidRef.current = call.parameters?.CallSid || null;
          if (from) {
            voiceAPI.lookup(from).then((r) => {
              const m = r.data?.match;
              if (m) {
                setIncomingName(m.name || '');
                setMatch(m);
                ctxRef.current = { number: from, contact_type: m.type, contact_id: m.id, contact_name: m.name, ivr_lang: ctxLang, ivr_reason: ctxReason };
              }
            }).catch(() => {});
          }
          call.on('cancel', () => { setIncoming(null); setIncomingName(''); setMatch(null); setCallContext(null); });
          call.on('disconnect', () => { callRef.current = null; setIncoming(null); setIncomingName(''); setStatus('ready'); setMuted(false); setNoteFor(ctxRef.current); });
          call.on('error', () => { setIncoming(null); setIncomingName(''); setMatch(null); setCallContext(null); });
        });
        await device.register();
        // Presence heartbeat so inbound calls know this admin is online AND
        // available. When the agent flips to Inactive we send available=false.
        const ping = () => { voiceAPI.presence(availableRef.current).catch(() => {}); };
        ping();
        presenceTimer = setInterval(() => {
          if (document.visibilityState === 'visible') ping();
        }, 25000);
      } catch (_) { /* softphone stays hidden if setup/token fails */ }
    })();

    return () => {
      disposed = true;
      if (presenceTimer) clearInterval(presenceTimer);
      try { device?.destroy(); } catch (_) { /* noop */ }
      deviceRef.current = null;
    };
  }, [user, getToken]);

  const dial = useCallback(async (number, context) => {
    const target = normalizeToE164(number || to || '');
    if (!target) {
      toast.error('Enter a valid US/PR number (10 digits) or +country code for international');
      return;
    }
    if (!deviceRef.current) { toast.error('Softphone is not ready'); return; }
    try {
      ctxRef.current = context ? { ...context, number: target } : { number: target };
      // Show who we're calling. Use the provided contact name if a Call
      // button passed one, otherwise look the number up in the CRM.
      setCallTarget({ number: target, name: context?.contact_name || '', match: null });
      if (!context?.contact_name) {
        voiceAPI.lookup(target).then((r) => {
          const m = r.data?.match;
          if (m) {
            setCallTarget((t) => (t ? { ...t, name: m.name || t.name, match: m } : t));
            ctxRef.current = { ...ctxRef.current, contact_type: m.type, contact_id: m.id, contact_name: m.name };
          }
        }).catch(() => {});
      }
      const params = { To: target };
      if (fromNumber) params.From = fromNumber;
      const call = await deviceRef.current.connect({ params });
      callRef.current = call;
      callSidRef.current = call.parameters?.CallSid || null;
      setStatus('calling');
      call.on('accept', () => setStatus('in-call'));
      call.on('disconnect', () => { callRef.current = null; setStatus('ready'); setMuted(false); setCallTarget(null); setNoteFor(ctxRef.current); });
      call.on('error', (e) => { toast.error(e?.message || 'Call error'); setCallTarget(null); });
    } catch (e) {
      setCallTarget(null);
      toast.error(e?.message || 'Could not start the call');
    }
  }, [to, fromNumber]);

  // Expose a global dialer so Call buttons elsewhere can use the softphone.
  useEffect(() => {
    if (!eligible) return undefined;
    window.krSoftphoneDial = (number, context) => {
      setOpen(true);
      setTo(number || '');
      if (number) dial(number, context);
    };
    return () => { delete window.krSoftphoneDial; };
  }, [eligible, dial]);

  // Audible ringtone while a call is incoming (US ringback 440+480 Hz).
  useEffect(() => {
    if (!incoming) return undefined;
    let ctx; let timer; let stopped = false;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      ctx = new AC();
      const beep = () => {
        if (stopped || !ctx) return;
        const t = ctx.currentTime;
        const g = ctx.createGain();
        g.connect(ctx.destination);
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.22, t + 0.05);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 1.6);
        [440, 480].forEach((f) => {
          const o = ctx.createOscillator();
          o.type = 'sine'; o.frequency.value = f;
          o.connect(g); o.start(t); o.stop(t + 1.7);
        });
      };
      beep();
      timer = setInterval(beep, 3000);
    } catch (_) { /* noop */ }
    return () => {
      stopped = true;
      if (timer) clearInterval(timer);
      try { ctx && ctx.close(); } catch (_) { /* noop */ }
    };
  }, [incoming]);

  const hangup = () => { try { callRef.current?.disconnect(); } catch (_) { /* noop */ } };
  const toggleAvailable = () => {
    setAvailable((prev) => {
      const next = !prev;
      try { localStorage.setItem('kr.softphone.available', next ? '1' : '0'); } catch (_) { /* noop */ }
      availableRef.current = next;
      voiceAPI.presence(next).catch(() => {});
      toast[next ? 'success' : 'message'](next ? 'You are now Active — inbound calls will ring here' : 'You are now Inactive — inbound calls will not ring here');
      return next;
    });
  };
  const toggleMute = () => {
    const next = !muted;
    try { callRef.current?.mute(next); setMuted(next); } catch (_) { /* noop */ }
  };

  // Aircall-style pause/resume of the live recording (e.g. while taking a
  // card number). The paused segment is skipped server-side, so the final
  // recording is one continuous file.
  const toggleRecording = async () => {
    if (recPending) return;
    const sid = callRef.current?.parameters?.CallSid || callSidRef.current;
    if (!sid) { toast.error('No active call'); return; }
    const next = !recordingPaused;
    setRecPending(true);
    try {
      await voiceAPI.pauseRecording({ call_sid: sid, pause: next });
      setRecordingPaused(next);
      toast[next ? 'message' : 'success'](next ? 'Recording paused' : 'Recording resumed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not change recording');
    } finally {
      setRecPending(false);
    }
  };

  const openTransfer = () => {
    setXfer({ stage: 'pick' });
    voiceAPI.online().then((r) => setXferAgents((r.data.items || []).filter((a) => a.user_id))).catch(() => setXferAgents([]));
  };
  const startTransfer = async (type, value) => {
    const customerSid = callRef.current?.parameters?.CallSid || callSidRef.current;
    if (!customerSid) { toast.error('No active call to transfer'); return; }
    try {
      const r = await voiceAPI.transferStart({ customer_call_sid: customerSid, target_type: type, target_value: value });
      // Agent A leaves the direct call and joins the conference to consult.
      try { callRef.current?.disconnect(); } catch (_) { /* noop */ }
      const call = await deviceRef.current.connect({ params: r.data.join_param });
      callRef.current = call;
      call.on('disconnect', () => { callRef.current = null; setStatus('ready'); setXfer(null); });
      setXfer({ stage: 'consult', conference: r.data.conference, customer_call_sid: customerSid });
      toast.success('Consulting — customer is on hold');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Transfer failed');
      setXfer(null);
    }
  };
  const completeTransfer = async () => {
    try {
      await voiceAPI.transferComplete({ conference: xfer.conference, customer_call_sid: xfer.customer_call_sid });
      try { callRef.current?.disconnect(); } catch (_) { /* noop */ }
      setXfer(null);
      toast.success('Transfer completed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not complete transfer');
    }
  };
  const accept = () => {
    if (!incoming) return;
    callRef.current = incoming;
    incoming.accept();
    setIncoming(null);
    setStatus('in-call');
  };
  const reject = () => { try { incoming?.reject(); } catch (_) { /* noop */ } setIncoming(null); };

  // iPhone-style keypad: type a number when idle, send DTMF tones mid-call.
  const sendOrType = (k) => {
    if (status === 'in-call' || status === 'calling') {
      try { callRef.current?.sendDigits(k); } catch (_) { /* noop */ }
    } else {
      setTo((v) => (v || '') + k);
    }
  };
  const backspace = () => setTo((v) => (v || '').slice(0, -1));

  const saveNote = async () => {
    if (!noteText.trim()) { setNoteFor(null); return; }
    setSavingNote(true);
    try {
      await voiceAPI.addCallNote({
        note: noteText.trim(),
        number: noteFor?.number,
        contact_type: noteFor?.contact_type,
        contact_id: noteFor?.contact_id,
        call_sid: callSidRef.current,
      });
      toast.success('Call note saved');
    } catch (_) {
      toast.error('Could not save the note');
    } finally {
      setSavingNote(false);
      setNoteText('');
      setNoteFor(null);
    }
  };

  const isLeadDispo = !!(noteFor && noteFor.contact_type === 'lead' && noteFor.contact_id);
  const isClientDispo = !!(noteFor && noteFor.contact_type === 'client' && noteFor.contact_id);
  const isProspectDispo = !!(noteFor && noteFor.contact_type === 'prospect' && noteFor.contact_id);
  const isDispo = isLeadDispo || isClientDispo || isProspectDispo;

  const resetDispo = () => {
    setDispoStatus(''); setDispoReason(''); setDispoOutcome(''); setCallbackAt('');
    setNoteText(''); setNoteFor(null);
    setSmsMeta(null); setSmsEnabled(false); setSmsBody(''); smsTouchedRef.current = false;
  };

  const saveDisposition = async () => {
    if (isProspectDispo) {
      if (!dispoOutcome) { toast.error('Select how the call went'); return; }
      setSavingDispo(true);
      try {
        await prospectAPI.logCall(noteFor.contact_id, {
          outcome: dispoOutcome,
          note: noteText.trim() || undefined,
          status: dispoStatus || undefined,
          callback_date: callbackAt ? callbackAt.slice(0, 10) : undefined,
          number: noteFor?.number,
          call_sid: callSidRef.current,
        });
        toast.success('Recruiting call logged');
        try { window.dispatchEvent(new CustomEvent('kr:prospect-updated', { detail: { prospectId: noteFor.contact_id } })); } catch (_) { /* noop */ }
        resetDispo();
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Could not log the call');
      } finally {
        setSavingDispo(false);
      }
      return;
    }
    if (isLeadDispo && dispositionRequired && !dispoStatus) {
      toast.error('Select a lead status before saving');
      return;
    }
    if (isLeadDispo && dispoStatus === 'not_interested' && !dispoReason) {
      toast.error('Select a reason for “Not interested”');
      return;
    }
    setSavingDispo(true);
    const contactId = noteFor.contact_id;
    const note = noteText.trim();
    try {
      // Attach the note to the call/comm record + mirror to the contact.
      if (note) {
        await voiceAPI.addCallNote({
          note,
          number: noteFor?.number,
          contact_type: noteFor?.contact_type,
          contact_id: noteFor?.contact_id,
          call_sid: callSidRef.current,
        }).catch(() => {});
      }
      // Lead-only status change.
      if (isLeadDispo) {
        if (dispoStatus === 'not_interested') {
          await leadsAPI.notInterested(contactId, dispoReason, note || undefined);
        } else if (dispoStatus) {
          await leadsAPI.changeStatus(contactId, dispoStatus, note || undefined);
        }
      }
      // Schedule a call-back reminder when a date was chosen (lead or client).
      if (callbackAt) {
        const iso = new Date(callbackAt).toISOString();
        const followNote = note || 'Call back (from softphone)';
        if (isClientDispo) {
          await clientsAPI.createFollowUp(contactId, iso, followNote);
        } else {
          await leadsAPI.createFollowUp(contactId, iso, followNote);
        }
      }
      // Optional follow-up SMS (leads only, when allowed + enabled).
      if (isLeadDispo && smsEnabled && smsMeta?.canSms && smsBody.trim()) {
        try {
          await smsMessagingAPI.send({ contact_type: 'lead', contact_id: contactId, body: smsBody.trim() });
          toast.success('Follow-up SMS sent');
        } catch (smsErr) {
          const d = smsErr?.response?.data?.detail;
          toast.error((d && (d.reason || d)) || 'Could not send the follow-up SMS');
        }
      }
      toast.success('Disposition saved');
      resetDispo();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save the disposition');
    } finally {
      setSavingDispo(false);
    }
  };

  // When the disposition panel opens for a lead, load SMS eligibility
  // (permission + consent + phone) and the language for the template.
  useEffect(() => {
    if (!isLeadDispo) return undefined;
    let cancelled = false;
    const leadId = noteFor.contact_id;
    setSmsMeta(null); setSmsEnabled(false); setSmsBody(''); smsTouchedRef.current = false;
    (async () => {
      try {
        const [leadRes, permRes] = await Promise.all([
          leadsAPI.get(leadId),
          smsMessagingAPI.permissions().catch(() => ({ data: { can_sms_leads: false } })),
        ]);
        if (cancelled) return;
        const lead = leadRes.data || {};
        const phone = lead.phone || '';
        const consentStatus = (lead.sms_consent && lead.sms_consent.status) || 'not_opted_in';
        const canSmsPerm = !!(permRes.data && permRes.data.can_sms_leads);
        const lang = resolveLang(lead.preferred_language, noteFor.ivr_lang);
        let reason = '';
        if (!canSmsPerm) reason = 'You do not have permission to SMS leads';
        else if (!phone) reason = 'Lead has no phone number on file';
        else if (consentStatus !== 'opted_in') reason = 'Lead has not opted in to SMS';
        setSmsMeta({
          canSms: canSmsPerm && !!phone && consentStatus === 'opted_in',
          reason, phone, consentStatus, lang,
          firstName: lead.first_name || noteFor.contact_name || '',
        });
      } catch (_) {
        if (!cancelled) setSmsMeta({ canSms: false, reason: 'Could not check SMS eligibility', lang: 'en', firstName: '' });
      }
    })();
    return () => { cancelled = true; };
  }, [isLeadDispo, noteFor?.contact_id]);

  // Keep the SMS body in sync with the template until the agent edits it.
  useEffect(() => {
    if (smsEnabled && smsMeta && !smsTouchedRef.current) {
      setSmsBody(buildFollowUpSms(smsMeta.lang, smsMeta.firstName, callbackAt ? new Date(callbackAt).toISOString() : ''));
    }
  }, [smsEnabled, smsMeta, callbackAt]);

  if (!eligible) return null;
  const inCall = status === 'calling' || status === 'in-call';

  return (
    <div className="fixed bottom-20 right-5 z-[70]" data-testid="softphone-widget">
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={`w-12 h-12 rounded-full text-white shadow-lg flex items-center justify-center relative ${available ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-400 hover:bg-slate-500'}`}
          title={available ? 'Softphone — Active' : 'Softphone — Inactive'}
          data-testid="softphone-launcher"
        >
          <Phone size={20} />
          {incoming && <span className="absolute -top-1 -right-1 w-3 h-3 bg-rose-500 rounded-full animate-ping" />}
          {!available && <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-slate-600 border-2 border-white rounded-full" />}
        </button>
      )}
      {open && (
        <div className="w-72 rounded-xl bg-white shadow-2xl border border-slate-200 overflow-hidden" data-testid="softphone-panel">
          <div className="flex items-center justify-between px-3 py-2 bg-[#0f2547] text-white">
            <span className="text-sm font-semibold flex items-center gap-2"><PhoneCall size={15} /> Softphone</span>
            <button onClick={() => setOpen(false)} className="text-white/80 hover:text-white" data-testid="softphone-close"><X size={16} /></button>
          </div>
          <div className="p-3 space-y-3">
            {phoneConfigured && (
              <button
                type="button"
                onClick={toggleAvailable}
                disabled={inCall}
                className={`w-full flex items-center justify-between rounded-lg border px-3 py-2 transition-colors disabled:opacity-60 ${
                  available
                    ? 'border-emerald-300 bg-emerald-50 hover:bg-emerald-100'
                    : 'border-slate-300 bg-slate-50 hover:bg-slate-100'
                }`}
                data-testid="softphone-availability-toggle"
                title="Toggle whether inbound calls ring your softphone"
              >
                <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <span className={`w-2.5 h-2.5 rounded-full ${available ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                  {available ? 'Active' : 'Inactive'}
                </span>
                <span className="text-[11px] text-slate-500">
                  {available ? 'Receiving calls' : 'Not receiving calls'}
                </span>
              </button>
            )}
            <div className="text-[11px] uppercase tracking-wider text-slate-400">
              {!phoneConfigured ? 'Not set up' : status === 'in-call' ? 'On call' : status === 'calling' ? 'Calling…' : ready ? 'Ready' : 'Connecting…'}
            </div>
            {!phoneConfigured && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2" data-testid="softphone-not-setup">
                Calling isn't set up yet. A KR SuperAdmin must run "Set up calling" in Settings → Calling.
              </div>
            )}
            {incoming && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="softphone-incoming">
                <div className="text-xs text-emerald-900 flex items-center gap-1.5 mb-2">
                  <PhoneIncoming size={14} /> Incoming call{incomingName ? ` from ${incomingName}` : (incoming.parameters?.From ? ` from ${incoming.parameters.From}` : '')}
                </div>
                {callContext && (callContext.reason || callContext.lang) && (
                  <div className="flex flex-wrap gap-1.5 mb-2" data-testid="softphone-incoming-context">
                    {callContext.reason && (
                      <span className="text-[11px] font-semibold rounded-full bg-violet-100 text-violet-800 border border-violet-200 px-2 py-0.5">
                        {callContext.reason}
                      </span>
                    )}
                    {callContext.lang && (
                      <span className="text-[11px] font-semibold rounded-full bg-sky-100 text-sky-800 border border-sky-200 px-2 py-0.5">
                        🗣 {callContext.lang}
                      </span>
                    )}
                  </div>
                )}
                {match && profilePath(match.type, match.id) && (
                  <button
                    type="button"
                    onClick={() => navigate(profilePath(match.type, match.id))}
                    className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-900 underline mb-2"
                    data-testid="softphone-view-profile"
                  >
                    <ExternalLink size={12} /> View {match.type} profile
                  </button>
                )}
                <div className="flex gap-2">
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={accept} data-testid="softphone-accept">Answer</Button>
                  <Button size="sm" variant="destructive" onClick={reject} data-testid="softphone-reject">Decline</Button>
                </div>
              </div>
            )}
            {noteFor && !incoming && !isDispo && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="softphone-note-block">
                <div className="text-xs text-slate-700 flex items-center gap-1.5 mb-2">
                  <FileText size={14} /> Call note {noteFor?.contact_name ? `— ${noteFor.contact_name}` : (noteFor?.number ? `(${noteFor.number})` : '')}
                </div>
                {noteFor && profilePath(noteFor.contact_type, noteFor.contact_id) && (
                  <button
                    type="button"
                    onClick={() => navigate(profilePath(noteFor.contact_type, noteFor.contact_id))}
                    className="inline-flex items-center gap-1 text-xs font-medium text-[#0f2547] hover:underline mb-2"
                    data-testid="softphone-note-view-profile"
                  >
                    <ExternalLink size={12} /> View {noteFor.contact_type} profile
                  </button>
                )}
                <Textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Write a note…"
                  rows={3}
                  className="text-sm mb-2"
                  data-testid="softphone-note-input"
                />
                <div className="flex gap-2">
                  <Button size="sm" className="flex-1 bg-[#0f2547]" onClick={saveNote} disabled={savingNote} data-testid="softphone-note-save">Save</Button>
                  <Button size="sm" variant="outline" onClick={() => { setNoteFor(null); setNoteText(''); }} data-testid="softphone-note-skip">Skip</Button>
                </div>
              </div>
            )}
            {noteFor && !incoming && isDispo && (
              <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-3" data-testid="softphone-disposition">
                <div className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                  <ClipboardCheck size={14} /> Wrap up call {noteFor?.contact_name ? `— ${noteFor.contact_name}` : ''}
                </div>
                <button
                  type="button"
                  onClick={() => navigate(profilePath(noteFor.contact_type, noteFor.contact_id))}
                  className="inline-flex items-center gap-1 text-xs font-medium text-[#0f2547] hover:underline"
                  data-testid="softphone-dispo-view-profile"
                >
                  <ExternalLink size={12} /> View {noteFor.contact_type} profile
                </button>

                {isProspectDispo && (
                <div data-testid="softphone-prospect-dispo">
                  <label className="text-[10px] uppercase tracking-wider text-slate-400">How did the call go? *</label>
                  <select
                    value={dispoOutcome}
                    onChange={(e) => setDispoOutcome(e.target.value)}
                    className="w-full mt-0.5 text-sm border border-slate-200 rounded-lg py-1.5 px-2 bg-white"
                    data-testid="softphone-dispo-outcome"
                  >
                    <option value="">Select outcome…</option>
                    {PROSPECT_OUTCOME_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <label className="text-[10px] uppercase tracking-wider text-slate-400 mt-2 block">Advance recruiting stage</label>
                  <select
                    value={dispoStatus}
                    onChange={(e) => setDispoStatus(e.target.value)}
                    className="w-full mt-0.5 text-sm border border-slate-200 rounded-lg py-1.5 px-2 bg-white"
                    data-testid="softphone-dispo-prospect-stage"
                  >
                    <option value="">No change</option>
                    {PROSPECT_STAGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                )}

                {isLeadDispo && (
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-400">
                    Lead status{dispositionRequired ? ' *' : ''}
                  </label>
                  <select
                    value={dispoStatus}
                    onChange={(e) => { setDispoStatus(e.target.value); if (e.target.value !== 'not_interested') setDispoReason(''); }}
                    className="w-full mt-0.5 text-sm border border-slate-200 rounded-lg py-1.5 px-2 bg-white"
                    data-testid="softphone-dispo-status"
                  >
                    <option value="">{dispositionRequired ? 'Select a status…' : 'No change'}</option>
                    {LEAD_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                )}

                {isLeadDispo && dispoStatus === 'not_interested' && (
                  <div data-testid="softphone-dispo-reason-block">
                    <label className="text-[10px] uppercase tracking-wider text-slate-400">Reason *</label>
                    <select
                      value={dispoReason}
                      onChange={(e) => setDispoReason(e.target.value)}
                      className="w-full mt-0.5 text-sm border border-slate-200 rounded-lg py-1.5 px-2 bg-white"
                      data-testid="softphone-dispo-reason"
                    >
                      <option value="">Select a reason…</option>
                      {NOT_INTERESTED_REASON_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                )}

                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1">
                    <CalendarClock size={11} /> Call back
                  </label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {[['tomorrow', 'Tomorrow'], ['3days', 'In 3 days'], ['week', 'Next week']].map(([k, lbl]) => {
                      const active = callbackAt === toLocalInput(callbackPreset(k));
                      return (
                        <button
                          key={k}
                          type="button"
                          onClick={() => setCallbackAt(toLocalInput(callbackPreset(k)))}
                          className={`text-[11px] font-medium rounded-full px-2.5 py-1 border transition-colors ${active ? 'bg-[#0f2547] text-white border-[#0f2547]' : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'}`}
                          data-testid={`softphone-dispo-callback-${k}`}
                        >
                          {lbl}
                        </button>
                      );
                    })}
                    {callbackAt && (
                      <button
                        type="button"
                        onClick={() => setCallbackAt('')}
                        className="text-[11px] font-medium rounded-full px-2.5 py-1 border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
                        data-testid="softphone-dispo-callback-clear"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <input
                    type="datetime-local"
                    value={callbackAt}
                    onChange={(e) => setCallbackAt(e.target.value)}
                    className="w-full mt-1.5 text-sm border border-slate-200 rounded-lg py-1.5 px-2 bg-white"
                    data-testid="softphone-dispo-callback-input"
                  />
                </div>

                <Textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Notes (optional)…"
                  rows={2}
                  className="text-sm"
                  data-testid="softphone-dispo-note"
                />

                {isLeadDispo && (
                <div className="rounded-lg border border-slate-200 p-2.5" data-testid="softphone-dispo-sms-block">
                  <label className={`flex items-center justify-between gap-2 ${smsMeta && !smsMeta.canSms ? 'opacity-60' : 'cursor-pointer'}`}>
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
                      <MessageSquare size={13} /> Send follow-up SMS
                      {smsMeta?.canSms && (
                        <span className="text-[10px] font-medium rounded-full bg-slate-100 text-slate-600 px-1.5 py-0.5 uppercase">
                          {smsMeta.lang === 'es' ? 'Español' : 'English'}
                        </span>
                      )}
                    </span>
                    <input
                      type="checkbox"
                      checked={smsEnabled}
                      disabled={!smsMeta || !smsMeta.canSms}
                      onChange={(e) => { setSmsEnabled(e.target.checked); if (!e.target.checked) smsTouchedRef.current = false; }}
                      className="h-4 w-4 accent-[#0f2547]"
                      data-testid="softphone-dispo-sms-toggle"
                    />
                  </label>
                  {!smsMeta && (
                    <p className="text-[11px] text-slate-400 mt-1" data-testid="softphone-dispo-sms-loading">Checking SMS eligibility…</p>
                  )}
                  {smsMeta && !smsMeta.canSms && (
                    <p className="text-[11px] text-amber-700 mt-1" data-testid="softphone-dispo-sms-reason">{smsMeta.reason}</p>
                  )}
                  {smsEnabled && smsMeta?.canSms && (
                    <Textarea
                      value={smsBody}
                      onChange={(e) => { smsTouchedRef.current = true; setSmsBody(e.target.value); }}
                      rows={3}
                      className="text-sm mt-2"
                      placeholder="Follow-up message…"
                      data-testid="softphone-dispo-sms-body"
                    />
                  )}
                </div>
                )}

                <div className="flex gap-2">
                  <Button size="sm" className="flex-1 bg-[#0f2547]" onClick={saveDisposition} disabled={savingDispo} data-testid="softphone-dispo-save">Save disposition</Button>
                  {(!dispositionRequired || !isLeadDispo) && (
                    <Button size="sm" variant="outline" onClick={resetDispo} data-testid="softphone-dispo-skip">Skip</Button>
                  )}
                </div>
              </div>
            )}
            {!noteFor && !incoming && (
              <div className="space-y-3">
                {inCall && (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-center" data-testid="softphone-active-call">
                    <div className="text-[11px] text-emerald-700 flex items-center justify-center gap-1.5 uppercase tracking-wider">
                      <PhoneCall size={13} /> {status === 'calling' ? 'Calling…' : 'On call with'}
                    </div>
                    <div className="text-base font-semibold text-emerald-900 mt-1" data-testid="softphone-active-call-name">
                      {callTarget?.name || formatUsDisplay(callTarget?.number) || callTarget?.number || 'Unknown'}
                    </div>
                    {callTarget?.name && (
                      <div className="text-xs text-emerald-700">{formatUsDisplay(callTarget.number)}</div>
                    )}
                    {callTarget?.match && profilePath(callTarget.match.type, callTarget.match.id) && (
                      <button
                        type="button"
                        onClick={() => navigate(profilePath(callTarget.match.type, callTarget.match.id))}
                        className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-900 underline mt-1"
                        data-testid="softphone-active-view-profile"
                      >
                        <ExternalLink size={12} /> View {callTarget.match.type} profile
                      </button>
                    )}
                    {fromNumber && (
                      <div className="text-[11px] text-emerald-700/80 mt-2 pt-2 border-t border-emerald-200" data-testid="softphone-active-from">
                        From: {numberNicknames[fromNumber] && numberNicknames[fromNumber] !== fromNumber
                          ? `${numberNicknames[fromNumber]} (${formatUsDisplay(fromNumber)})`
                          : formatUsDisplay(fromNumber)}
                      </div>
                    )}
                  </div>
                )}
                {!inCall && callerNumbers.length > 1 && (
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-slate-400">Call from</label>
                    <select
                      value={fromNumber}
                      onChange={(e) => {
                        setFromNumber(e.target.value);
                        try { localStorage.setItem(fromStorageKey, e.target.value); } catch (_) { /* noop */ }
                      }}
                      className="w-full mt-0.5 text-sm border border-slate-200 rounded-lg py-1.5 px-2 bg-white"
                      data-testid="softphone-from-select"
                    >
                      {callerNumbers.map((n) => {
                        const nick = numberNicknames[n];
                        return <option key={n} value={n}>{nick && nick !== n ? `${nick} — ${n}` : n}</option>;
                      })}
                    </select>
                  </div>
                )}
                {!inCall && callerNumbers.length === 1 && fromNumber && (
                  <div className="flex items-center gap-2 text-xs bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5"
                       data-testid="softphone-single-from">
                    <span className="text-[10px] uppercase tracking-wider text-slate-400">Call from</span>
                    <span className="font-semibold text-slate-700">
                      {numberNicknames[fromNumber] && numberNicknames[fromNumber] !== fromNumber
                        ? `${numberNicknames[fromNumber]} · ${formatUsDisplay(fromNumber)}`
                        : formatUsDisplay(fromNumber)}
                    </span>
                  </div>
                )}
                {!inCall && (
                <div className="relative">
                  <input
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && ready) dial(); }}
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="(863) 555-1212"
                    className="w-full text-center text-lg font-mono tracking-wide bg-slate-50 border border-slate-200 rounded-lg py-2 px-8 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                    data-testid="softphone-number-display"
                  />
                  {to && (
                    <button
                      onClick={backspace}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      data-testid="softphone-backspace"
                    >
                      <Delete size={16} />
                    </button>
                  )}
                </div>
                )}
                <div className="grid grid-cols-3 gap-2" data-testid="softphone-keypad">
                  {['1', '2', '3', '4', '5', '6', '7', '8', '9', '+', '0', '#'].map((k) => (
                    <button
                      key={k}
                      onClick={() => sendOrType(k)}
                      data-testid={`softphone-key-${k}`}
                      className="h-11 rounded-lg bg-slate-100 hover:bg-slate-200 text-lg font-semibold text-slate-800 active:scale-95 transition-transform"
                    >
                      {k}
                    </button>
                  ))}
                </div>
                {!inCall ? (
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-700 h-11" onClick={() => dial()} disabled={!ready} data-testid="softphone-call-btn">
                    <Phone size={16} className="mr-1" /> Call
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                    <Button variant="outline" onClick={toggleMute} className="flex-1 h-11" data-testid="softphone-mute-btn">
                      {muted ? <MicOff size={14} className="mr-1" /> : <Mic size={14} className="mr-1" />}
                      {muted ? 'Unmute' : 'Mute'}
                    </Button>
                    <Button variant="destructive" onClick={hangup} className="flex-1 h-11" data-testid="softphone-hangup-btn">
                      <PhoneOff size={14} className="mr-1" /> Hang up
                    </Button>
                    </div>
                    {canPauseRec && recordingEnabled && (
                      <Button
                        variant="outline"
                        className={`w-full h-9 text-xs ${recordingPaused ? 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100' : ''}`}
                        onClick={toggleRecording}
                        disabled={recPending}
                        data-testid="softphone-recording-toggle"
                      >
                        {recordingPaused
                          ? <><Play size={13} className="mr-1" /> Resume recording</>
                          : <><Pause size={13} className="mr-1" /> Pause recording</>}
                      </Button>
                    )}
                    {recordingPaused && (
                      <div className="text-[11px] text-amber-700 text-center -mt-1" data-testid="softphone-recording-paused-note">
                        Recording paused — this part won't be saved
                      </div>
                    )}
                    {!xfer && (
                      <Button variant="outline" className="w-full h-9 text-xs" onClick={openTransfer} data-testid="softphone-transfer-btn">
                        <ArrowRightLeft size={13} className="mr-1" /> Warm transfer
                      </Button>
                    )}
                    {xfer && xfer.stage === 'pick' && (
                      <div className="rounded-lg border border-slate-200 p-2 space-y-2" data-testid="softphone-transfer-pick">
                        <div className="text-[11px] font-semibold text-slate-500 uppercase">Transfer to</div>
                        <div className="max-h-24 overflow-auto space-y-1">
                          {xferAgents.map((a) => (
                            <button key={a.user_id} onClick={() => startTransfer('agent', a.user_id)} className="w-full text-left text-sm px-2 py-1 rounded hover:bg-slate-100" data-testid={`softphone-xfer-agent-${a.user_id}`}>
                              👤 {a.name}
                            </button>
                          ))}
                          {xferAgents.length === 0 && <div className="text-xs text-slate-400 px-1">No available agents</div>}
                        </div>
                        <div className="flex gap-1">
                          <input value={xferNumber} onChange={(e) => setXferNumber(e.target.value)} placeholder="+1 number" className="flex-1 text-sm border rounded px-2 py-1" data-testid="softphone-xfer-number" />
                          <Button size="sm" onClick={() => startTransfer('number', xferNumber)} disabled={!xferNumber} data-testid="softphone-xfer-number-go">Go</Button>
                        </div>
                        <Button size="sm" variant="ghost" className="w-full text-xs" onClick={() => setXfer(null)}>Cancel</Button>
                      </div>
                    )}
                    {xfer && xfer.stage === 'consult' && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 space-y-2" data-testid="softphone-transfer-consult">
                        <div className="text-xs text-amber-800">Consulting… customer is on hold. Talk, then complete.</div>
                        <Button size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={completeTransfer} data-testid="softphone-xfer-complete">Complete transfer &amp; drop off</Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
